import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setFirebaseToken, putPushTokenAsync } from 'features/common/commonSlice';
import { selectHasProfessionalNotifications, selectUserLogged } from 'features/common/commonSelectors';
import { pushNotification } from 'features/notifications/notificationsSlice';
import { getUserUnreadNotificationsCountAsync } from 'features/userDetail/asyncActions';
import { onForegroundMessage, requestFcmToken } from 'services/firebase';
import { i18n } from 'utils/i18n';
import { RootState } from 'app/store';

export const useFirebaseMessaging = (): void => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUserLogged);
  const firebaseToken = useAppSelector((state: RootState) => state.common.firebaseToken);
  const hasNotifications = useAppSelector(selectHasProfessionalNotifications);
  const userIdRef = useRef<string | undefined>(user?.id);
  const hasNotificationsRef = useRef(hasNotifications);
  const lastRegisteredTokenRef = useRef<string | null>(null);

  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  useEffect(() => {
    hasNotificationsRef.current = hasNotifications;
  }, [hasNotifications]);

  useEffect(() => {
    if (!user?.id || !firebaseToken) return;
    if (lastRegisteredTokenRef.current === firebaseToken) return;
    dispatch(putPushTokenAsync(firebaseToken));
    lastRegisteredTokenRef.current = firebaseToken;
  }, [user?.id, firebaseToken, dispatch]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      const token = await requestFcmToken();
      if (cancelled) return;
      if (token) {
        dispatch(setFirebaseToken(token));
      }

      unsubscribe = await onForegroundMessage((payload) => {
        const data = payload.data ?? {};
        const myId = userIdRef.current;

        if (data.type === 'professional-notification' && myId) {
          // Sin notificaciones de profesionales (BILLING, INSTALLER) gdp-api deniega el contador: no se pide.
          if (hasNotificationsRef.current) {
            dispatch(getUserUnreadNotificationsCountAsync({ id: myId, contextKey: myId }));
          }

          const notifTitle = payload.notification?.title || data.title || '';
          const notifBody = payload.notification?.body || data.body || '';

          dispatch(
            pushNotification({
              title: notifTitle || undefined,
              message: notifBody,
              severity: 'info',
              variant: 'professional',
              persistent: true,
              senderName: data.senderName,
              action: { label: i18n('toast.notification.viewAction'), href: `/professional/${myId}?tab=communications` },
            }),
          );
          return;
        }

        const message =
          payload.notification?.body ||
          payload.notification?.title ||
          data.body ||
          data.title ||
          '';
        if (message) {
          dispatch(pushNotification({ message, severity: 'info' }));
        }
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [dispatch]);
};
